import { Button, DatePicker, Drawer, Form, Input } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceApp } from 'src/api/facade/types'
import type { AppCredentialRead } from 'src/api/main'

import { AppPermissionsSelect } from './AppPermissionsSelect'

export type EditFormValues = {
  name: string
  expiresAt?: Dayjs | null
  permission_codes?: string[]
}

type Props = {
  open: boolean
  /** App whose permissions the credential may grant. */
  app?: WorkspaceApp
  /** Credential being edited — the form prefills from this. */
  credential: AppCredentialRead | null
  submitting: boolean
  onSubmit: (values: EditFormValues) => void
  onClose: () => void
}

/**
 * Edit drawer for app credentials — name, expiry, and the granted permissions
 * are all editable in place (same secret, no re-distribution). Used from both
 * the credentials tab (list row's Edit button) and the credential detail page.
 */
export const AppCredentialEditDrawer = ({
  open,
  app,
  credential,
  submitting,
  onSubmit,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<EditFormValues>()

  // Prefill each time the drawer opens on a new credential — running this
  // in an effect (not on toggle) keeps the values consistent even if the
  // parent swaps out `credential` while the drawer is mounted.
  useEffect(() => {
    if (!open || !credential) return
    form.setFieldsValue({
      name: credential.name,
      expiresAt: credential.expires_at ? dayjs(credential.expires_at) : null,
      permission_codes: credential.permission_codes ?? []
    })
  }, [open, credential, form])

  return (
    <Drawer
      title={t('workspace.apps.credentials.editDrawerTitle')}
      open={open}
      size={420}
      destroyOnHidden
      onClose={onClose}
      extra={
        <Button
          type="primary"
          loading={submitting}
          onClick={() => form.submit()}
        >
          {t('save')}
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onSubmit}
      >
        <Form.Item
          label={t('name')}
          name="name"
          rules={[{ required: true, message: t('required_field_error') }]}
        >
          <Input placeholder={t('placeholders.credentialName')} />
        </Form.Item>
        <Form.Item
          label={t('workspace.apps.credentials.expiresAt')}
          name="expiresAt"
        >
          <DatePicker
            showTime
            style={{ width: '100%' }}
            placeholder={t('workspace.apps.credentials.expiresAtPlaceholder')}
            disabledDate={(current) => current && current.isBefore(dayjs())}
          />
        </Form.Item>
        <Form.Item
          label={t('workspace.apps.credentials.permissions')}
          name="permission_codes"
          extra={t('workspace.apps.credentials.permissionsHint')}
        >
          <AppPermissionsSelect app={app} />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
